import clsx from "clsx";
import dynamic from "next/dynamic";
import Link from "next/link";
import * as React from "react";
import { HTMLAttributeAnchorTarget, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePopper } from "react-popper";
import { Switch } from "@/components/ui/switch";
import { User } from "@/contexts/common/AuthContextProvider";
import { useAlert } from "@/hooks/useAlert";
import { useHydrationSafeFeatureFlagEnabled } from "@/hooks/useHydrationSafeFeatureFlagEnabled";
import { useOrg } from "@/hooks/useOrg";
import { BugReportResponse } from "@/types/admin/common";
import { GlobalNavSectionType } from "@/types/common/library";
import { IsSuperAdmin } from "@/utils/common/admin";
import { adminAppBaseUrl } from "@/utils/common/adminAppUrl";
import { apiFetch } from "@/lib/api";
import { BASE_FEEDBACK_URL } from "@/utils/common/baseUrls";
import { canUseAbly } from "@/utils/common/region";
import { getNameWithLastInitial } from "@/utils/library/formatters";
import { IconName } from "@fortawesome/fontawesome-common-types";
import { Popover } from "@headlessui/react";
import AlertDot from "../common/AlertDot";
import TrialUsageWidget from "../common/trial/TrialUsageWidget";
import NotificationCenterDialog from "../notifications/NotificationCenterDialog";
import Button from "./Button";
import GdprComplianceBadge from "./GdprComplianceBadge";
import { ICON_SIZES, ICON_TYPE, Icon } from "./Icon";
import Modal from "./Modal";
import { NayLayoutProps } from "./NavLayout";
import { GlobalNavigationItem } from "./NavLayoutItem";
import TextArea from "./TextArea";
import UserAvatar from "./UserAvatar";

const isInternalAppHref = (href: string) =>
  href.startsWith("/") && !href.startsWith("//");

const NavLayoutNotificationStatus = dynamic(() => import("./NavLayoutNotificationStatus"), {
  ssr: false,
});

type DefaultUserActionProps = {
  label: string;
  icon: IconName;
  hasAlert?: boolean;
  dataAttributes?: Record<`data-${string}`, string>;
  target?: HTMLAttributeAnchorTarget;
};

type UserActionProps = DefaultUserActionProps &
  (
    | {
        onClick: () => void;
        href?: never;
      }
    | {
        onClick?: never;
        href: string;
      }
  );

const UserAction: React.FC<UserActionProps> = ({
  label,
  onClick,
  href,
  icon,
  hasAlert,
  dataAttributes,
  target,
}) => {
  const className = clsx(
    "grid",
    "gap-2",
    "w-full",
    "p-2",
    "transition-all",
    "cursor-pointer",
    "hover:bg-slate-100",
    "focus:bg-slate-100",
    "active:bg-slate-100",
    "dark:hover:bg-slate-800",
    "dark:focus:bg-slate-800",
    "dark:active:bg-slate-800",
    "rounded-lg",
    {
      "grid-cols-[20px_1fr]": !hasAlert,
      "grid-cols-[20px_1fr_6px]": hasAlert,
    }
  );

  const content = (
    <>
      <div className="flex justify-center items-center">
        <Icon icon={icon} size={ICON_SIZES.SM} type={ICON_TYPE.LIGHT} />
      </div>
      <span className={clsx("text-sm", "mr-auto")}>{label}</span>
      {hasAlert && <AlertDot />}
    </>
  );

  // Internal app routes must use Next Link — a raw <a href="/profile"> forces
  // a full document reload and re-triggers SSR hydration mismatches.
  if (href && isInternalAppHref(href) && target !== "_blank") {
    return (
      <Link href={href} className={className} {...dataAttributes}>
        {content}
      </Link>
    );
  }

  return (
    <a
      onClick={onClick}
      href={href}
      {...dataAttributes}
      target={target}
      className={className}
    >
      {content}
    </a>
  );
};

type Props = {
  user: User;
  bottomSections?: GlobalNavSectionType[];
  hasAlert: boolean;
  isMinimized?: boolean;
} & Pick<NayLayoutProps, "userActions">;

const NavLayoutSidebarFooter: React.FC<Props & { onItemClick?: () => void }> = ({
  user,
  userActions,
  bottomSections,
  hasAlert,
  onItemClick,
  isMinimized = false,
}) => {
  const { setAlert } = useAlert();
  const { org } = useOrg();
  const ablyEnabled = canUseAbly();
  const notificationCenterEnabled = useHydrationSafeFeatureFlagEnabled(
    "ui-notification-center-enabled"
  );
  const { t } = useTranslation("routes");
  const { t: nt } = useTranslation("notifications");
  const { t: lt } = useTranslation("login");
  const { t: ct } = useTranslation("common");
  const adminAppUrl = adminAppBaseUrl();

  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [reportOpen, setOpenReport] = useState(false);
  const [bugReportText, setBugReportText] = useState("");
  const [markUrgent, setMarkUrgent] = useState(false);
  const [isAdmin, setIsAdmin] = useState(IsSuperAdmin(user));

  useEffect(() => {
    const adminTest = !!IsSuperAdmin(user);
    if (adminTest !== isAdmin) {
      setIsAdmin(adminTest);
    }
  }, [user]);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 16],
        },
      },
    ],
  });

  return (
    <div className={clsx("mt-auto")}>
      {!isMinimized && (
        <div
          className={clsx("mb-3")}
          style={
            {
              transition: "opacity var(--fade-duration)",
              opacity: isMinimized ? 0 : 1,
            } as React.CSSProperties
          }
        >
          <TrialUsageWidget variant="sidebar" />
        </div>
      )}
      {bottomSections && bottomSections.length > 0 && (
        <div className={clsx("mb-4", "mx-[-1rem]")}>
          {bottomSections.map((bottomSection, index) => (
            <div key={`bottom-section-${index}`}>
              {index === 1 && (
                <hr className={clsx("border-gray-200", "dark:border-gray-600", "my-4")} />
              )}
              {bottomSection.name && (
                <div className="relative mb-2" style={{ height: "20px" }}>
                  {/* Section title - visible when expanded */}
                  <label
                    style={
                      {
                        transition: "opacity var(--fade-duration)",
                        opacity: isMinimized ? 0 : 1,
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "20px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      } as React.CSSProperties
                    }
                    className={clsx("pl-4", "text-600", "text-sm", "mt-6", "block")}
                  >
                    {bottomSection.name}
                  </label>
                  {/* HR - visible when minimized */}
                  <hr
                    className="bg-300"
                    style={
                      {
                        transition: "opacity var(--fade-duration)",
                        opacity: isMinimized ? 1 : 0,
                        position: "absolute",
                        top: "0",
                        left: "16px",
                        right: "16px",
                        transform: "translateY(-50%)",
                        height: "1px",
                        border: "none",
                      } as React.CSSProperties
                    }
                  />
                </div>
              )}
              <div className={clsx("grid", "self-stretch")}>
                {bottomSection.items.map((item) => (
                  <GlobalNavigationItem
                    key={`${item.label}_${item.icon}`}
                    {...item}
                    onItemClick={onItemClick}
                    isMinimized={isMinimized}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <GdprComplianceBadge isMinimized={isMinimized} />
      <Popover>
        <Popover.Button
          ref={setReferenceElement}
          className={clsx(
            "flex",
            "items-center",
            "gap-2",
            "w-full",
            "block",
            // 16px aside padding + 4px + half the 24px avatar = 32px: dead
            // center of the minimized 64px rail.
            "pl-1",
            "outline-none",
            "focus:ring-px",
            "transition-all",
            "active:bg-slate-200"
          )}
          onClick={() => setOpen(!open)}
        >
          <UserAvatar img={user?.img} name={user.name} />
          <span
            className={clsx("text-sm", "whitespace-nowrap", "overflow-hidden", "text-ellipsis")}
            style={
              {
                transition: "opacity var(--fade-duration)",
                opacity: isMinimized ? 0 : 1,
                width: "100%",
                display: "inline-block",
                textAlign: "left",
              } as React.CSSProperties
            }
          >
            {getNameWithLastInitial(user?.name || "")}
          </span>
          {hasAlert && (
            <span>
              <AlertDot />
            </span>
          )}
          {notificationCenterEnabled && ablyEnabled && (
            <NavLayoutNotificationStatus
              onOpenNotificationDialog={() => setNotificationDialogOpen(true)}
            />
          )}
        </Popover.Button>
        <Popover.Panel
          className={clsx(
            "absolute",
            "z-30",
            "bg-gray-100",
            "dark:bg-gray-900",
            "md:bg-white",
            "rounded-lg",
            "drop-shadow-md",
            "border-1",
            "border-300",
            "md:ml-4"
          )}
          style={styles.popper}
          ref={setPopperElement}
          {...attributes.popper}
        >
          <div className={clsx("p-4")}>
            <p className="text-sm">{user?.name}</p>
            <p className={clsx("text-xs", "mt-1", "text-slate-500")}>{user.email}</p>
          </div>
          <hr />
          {!!userActions && (
            <ul className={clsx("p-2", "space-y-2")}>
              {notificationCenterEnabled && (
                <UserAction
                  label={nt("title")}
                  onClick={() => {
                    setNotificationDialogOpen(true);
                    setOpen(false);
                  }}
                  icon="bell"
                />
              )}
              <UserAction
                label={lt("profile")}
                href="/profile"
                icon="square-user"
                hasAlert={hasAlert}
              />
              {userActions?.onUserManagementClick && (
                <UserAction
                  label={t("switchOrg")}
                  onClick={userActions.onUserManagementClick}
                  icon="people-arrows"
                />
              )}
              <UserAction
                label={t("features")}
                href={`${BASE_FEEDBACK_URL}/changelog`}
                icon="lightbulb-on"
                dataAttributes={{ "data-canny-link": "true" }}
                target="_blank"
              />
              {IsSuperAdmin(user) && (
                <UserAction label={t("reportBug")} onClick={() => setOpenReport(true)} icon="bug" />
              )}
              {IsSuperAdmin(user) && adminAppUrl && (
                <UserAction
                  label={t("admin")}
                  href={adminAppUrl}
                  icon="user-crown"
                  target="_blank"
                />
              )}
              {userActions?.onLogoutClick && (
                <UserAction
                  label={lt("logout")}
                  onClick={userActions?.onLogoutClick}
                  icon="right-from-bracket"
                />
              )}
            </ul>
          )}
        </Popover.Panel>
      </Popover>
      {notificationCenterEnabled && (
        <NotificationCenterDialog
          open={notificationDialogOpen}
          onOpenChange={setNotificationDialogOpen}
        />
      )}
      <Modal
        open={reportOpen}
        onOpenChange={(open) => !open && setOpenReport(false)}
        header={{
          title: t("reportBug"),
          description:
            isAdmin && org?.id ? (
              <div className={clsx("text-xs", "text-600")}>Org ID: {org.id}</div>
            ) : undefined,
        }}
        footer={
          <div className="flex justify-end gap-2 w-full">
            {isAdmin && (
              <Button
                type="secondary"
                onClick={() => {
                  navigator.clipboard.writeText(org?.id || "");
                  setAlert({
                    status: "success",
                    message: lt("copiedOrgId"),
                    secondsToClose: 4,
                  });
                }}
              >
                {lt("copyOrgId")}
              </Button>
            )}
            <Button
              onClick={async () => {
                const apiReportResponse = await apiFetch<BugReportResponse>(
                  "/api/report-bug",
                  "POST",
                  {
                  body: {
                    url: window.location.href,
                    description: bugReportText,
                    urgent: markUrgent,
                  },
                },
                );

                if (!apiReportResponse || !apiReportResponse.success) {
                  setAlert({
                    status: "error",
                    message: apiReportResponse?.message || ct("errorReportingBug"),
                    secondsToClose: 10,
                  });
                  return;
                }

                setAlert({
                  status: "success",
                  message: apiReportResponse.message,
                  secondsToClose: 4,
                });

                setOpenReport(false);
                setMarkUrgent(false);
                setBugReportText("");
              }}
            >
              {t("reportBug")}
            </Button>
            <Button type="secondary" onClick={() => setOpenReport(false)}>
              {ct("cancel")}
            </Button>
          </div>
        }
      >
        <div className="mt-[-1rem]">
          <TextArea
            label={ct("description")}
            placeholder={t("reportBugPlaceholder")}
            value={bugReportText}
            onChange={(val) => setBugReportText(val)}
          />
        </div>
        <Switch
          label={ct("markUrgent")}
          checked={markUrgent}
          onCheckedChange={(checked) => setMarkUrgent(checked)}
          className="mt-2"
        />
      </Modal>
    </div>
  );
};

export default NavLayoutSidebarFooter;
